import styled, { css } from 'styled-components';
import { always, ifElse, propEq } from 'ramda';
import { Link } from 'gatsby';
import { Link as ScrollerLink } from 'react-scroll';

import {
  WHITE,
  TRANSPARENT,
  SIDEBAR_TEXT_COLOR,
  SIDEBAR_TITLE_COLOR,
  SIDEBAR_LINE_COLOR,
  SIDEBAR_DOT_TEXT_COLOR,
  CATEGORICAL_COLORS,
  UI_COLORS,
} from '../../utils/colors';
import { Logo as LogoBase } from '../logo';
import {
  DRAWER_OPEN,
  DRAWER_COLLAPSED,
  DRAWER_CONTENT_WIDTH,
  OUT_OFFSET,
  X_DRAWER_OPEN,
  X_DRAWER_COLLAPSED,
  ANIMATION_DURATION_TIME,
  VISIBLE,
  INVISIBLE,
} from './tableOfContents.constants';
import { media } from '../../utils/media';
import { ZINDEX } from '../../utils/constants';

export const getSectionColor = (index) => CATEGORICAL_COLORS[index] || SIDEBAR_LINE_COLOR;

const ifElseSelected = (styleTrue, styleFalse) => ifElse(propEq('selected', true), styleTrue, styleFalse);
const ifElseOpenFn = (styleTrue, styleFalse) => ifElse(propEq('open', true), styleTrue, styleFalse);
const ifElseOpen = (styleTrue, styleFalse) => ifElse(propEq('open', true), always(styleTrue), always(styleFalse));

const drawerWidthStyle = css`
  width: 100vw;
  background-color: ${WHITE};

  ${media.desktop`
    transition: width 0.3s ease-in;
    width: ${({ open }) => (open ? DRAWER_OPEN : DRAWER_COLLAPSED)}px;
  `}
`;

export const TableOfContentsWrapper = styled.div`
  @media print {
    display: none;
  }
`;

export const Logo = styled(LogoBase)`
  height: 28px;
  width: 100px;
  margin-bottom: 3px;
  cursor: pointer;
`;

export const ActionsList = styled.div`
  margin: 30px 0 0;

  ${media.desktop`
    margin: 0;
    width: ${DRAWER_CONTENT_WIDTH}px;
  `}
`;

export const ActionItem = styled.div`
  display: flex;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 300;
  letter-spacing: 0.6px;
  line-height: 20px;

  padding: 10px 0;
  margin: 3px 0;
  cursor: pointer;
  color: ${UI_COLORS[2]};

  svg {
    margin-right: 10px;
  }

  ${media.desktop`
    padding: 2px 0;
  `}
`;

export const ActionLabel = styled.span``;

export const DrawerContent = styled.div`
  border-right: 2px solid ${SIDEBAR_LINE_COLOR};
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 30px;

  ${media.desktop`
    padding: 20px;
    position: relative;
  `}
`;

export const OpenDrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  color: ${SIDEBAR_TEXT_COLOR};
  flex: 1 auto;
  overflow-y: auto;

  ${media.desktop`
    width: ${DRAWER_CONTENT_WIDTH}px;
  `}
`;

export const CollapsedDrawerContent = styled.div`
  position: absolute;
  top: 83px;
  display: none;

  ${media.desktop`
    display: block;
  `}
`;

export const CloseButton = styled.div`
  position: absolute;
  cursor: pointer;
  padding: 4px;
  top: 30px;
  right: 28px;

  ${media.desktop`
    top: 8px;
    right: 12px;
  `}
`;

export const Drawer = styled.div`
  ${drawerWidthStyle};
  position: fixed;
  z-index: ${ZINDEX.TABLE_OF_CONTENTS};
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  left: -100vw;
  top: 0;
  flex: 0 0 auto;
  transition: transform ${ANIMATION_DURATION_TIME}, opacity ${ANIMATION_DURATION_TIME};
  transform: translateX(${ifElseOpen('100vw', '0')});
  opacity: ${ifElseOpen(1, 0)};

  ${media.desktop`
    position: static;
    min-height: unset;
    transform: none;
    opacity: 1;
  `}

  ${DrawerContent} {
    ${drawerWidthStyle}
  }

  ${OpenDrawerContent} {
    ${media.desktop`
      transition: transform ${ANIMATION_DURATION_TIME} ease-in;
      transform: translateX(${ifElseOpen(X_DRAWER_OPEN, X_DRAWER_COLLAPSED)}px);
    `}
  }

  ${CollapsedDrawerContent} {
    ${media.desktop`
        transition: transform ${ANIMATION_DURATION_TIME} ease-in;
        transform: translateX(${ifElseOpen(-X_DRAWER_COLLAPSED, X_DRAWER_OPEN)}px);
    `}
  }

  ${CloseButton} {
    ${ifElseOpenFn(
      () => css`
        transition: opacity ${ANIMATION_DURATION_TIME} ease-in, transform 0s linear 0s;
        transform: translateY(0px);
        opacity: ${VISIBLE};
      `,
      () => css`
        transition: opacity ${ANIMATION_DURATION_TIME} ease-in, transform 0s linear ${ANIMATION_DURATION_TIME};
        transform: translateY(${OUT_OFFSET}px);
        opacity: ${INVISIBLE};
      `
    )}
  }

  ${Logo} {
    transition: transform ${ANIMATION_DURATION_TIME} ease-in;
    transform: translateX(${ifElseOpen(0, -5)}px);
  }

  .pefa-logo_svg__logo-text {
    ${ifElseOpenFn(
      () => css`
        transition: opacity ${ANIMATION_DURATION_TIME} ease-in;
        opacity: ${VISIBLE};
      `,
      () => css`
        transition: opacity ${ANIMATION_DURATION_TIME} ease-in;
        opacity: ${INVISIBLE};
      `
    )}
  }

  ${ActionLabel} {
    transition: opacity ${ANIMATION_DURATION_TIME} ease-in;
    opacity: ${ifElseOpen(VISIBLE, INVISIBLE)};
  }
`;

export const Title = styled.h2`
  font-size: 18px;
  font-weight: 300;
  line-height: 1.22em;
  color: ${SIDEBAR_TITLE_COLOR};
  margin-bottom: 39px;
  text-decoration: none;
  cursor: pointer;
`;

export const SectionList = styled.ul`
  flex: 1;
  height: 100%;
  overflow-y: auto;

  margin: 0;
  list-style: none;
`;

const setSectionTitleColor = (color) => css`
  color: ${color};
  border-bottom: 2px solid ${color};
`;

export const SectionTitle = styled.h5`
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.8em;
  letter-spacing: 0.25px;
  ${({ index }) => setSectionTitleColor(getSectionColor(index))}
`;

export const Counter = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.3px;
  color: ${SIDEBAR_DOT_TEXT_COLOR};
  margin-right: 12px;
  background-color: #ccc;
`;

const counterStyles = css`
  ${Counter} {
    ${ifElseSelected(
      ({ index }) => css`
        background-color: ${getSectionColor(index)};
        color: ${SIDEBAR_DOT_TEXT_COLOR};
      `,
      ({ index }) => css`
        background-color: ${TRANSPARENT};
        color: ${getSectionColor(index)};
      `
    )}
  }
`;

export const CounterLink = styled(Link)`
  text-decoration: none;
  margin-bottom: 20px;
  display: block;

  ${counterStyles}
`;

export const Section = styled.li`
  margin: 0 0 30px;

  ${media.desktop`
    margin: 0;
  `}
`;

export const SubSection = styled.div`
  ${counterStyles}
`;

export const SubSectionTitleWrapper = styled(Link)`
  display: flex;
  cursor: pointer;
  margin-bottom: 10px;
  text-decoration: none;
`;

export const SubSectionTitle = styled.h6`
  font-size: 16px;
  font-weight: 300;
  line-height: 20px;
  margin: 0;
  color: ${SIDEBAR_TEXT_COLOR};
`;

export const PagesList = styled.ul`
  margin: 0;
  list-style: none;
`;

export const DotSvg = styled.svg`
  margin: 5px 15px 0 1px;

  ${media.desktop`
    margin: 1px 15px 0 1px;
  `}
`;

export const DotCircle = styled.circle`
  stroke-width: 1;
`;

export const Page = styled.div`
  display: flex;
  width: 100%;
  font-size: 14px;
  line-height: 16px;
  padding: 5px;
  border-bottom: 2px solid ${SIDEBAR_LINE_COLOR};

  ${media.desktop`
    font-size: 12px;
    padding: 2px;
  `}
`;

export const PageItem = styled.li`
  margin: 0;

  ${DotCircle} {
    stroke: ${({ index }) => getSectionColor(index)};

    ${ifElseSelected(
      ({ index }) => css`
        fill: ${getSectionColor(index)};
      `,
      () => css`
        fill: ${TRANSPARENT};
      `
    )}
  }

  ${Page} {
    ${ifElseSelected(
      ({ index }) => css`
        color: ${getSectionColor(index)};
      `,
      () => css`
        color: ${SIDEBAR_TEXT_COLOR};
      `
    )}
  }

  :first-child ${Page} {
    border-top: 2px solid ${SIDEBAR_LINE_COLOR};
  }

  :last-child {
    margin-bottom: 15px;
  }
`;

export const HiddenLink = styled(ScrollerLink)`
  display: none;
`;

export const PageLink = styled(ScrollerLink)`
  display: flex;
  text-decoration: none;
  cursor: pointer;
`;
